import * as THREE from "three";

/**
 * Cloud shadows: big soft patches of shade drifting across the battlefield (lighting review F7).
 *
 * The field had no large-scale light variation at all — the only low-frequency change was the ground's macro tint —
 * so a still camera looked like a still photograph. A procedural value-noise field (no sampler: iOS gives us 16 and
 * the ground already spends most of them) dims the DIRECT light only, so shaded ground keeps its sky fill and simply
 * reads as "a cloud went over". Cells are ~20 units across and cross a phone screen in about a minute.
 *
 * One module, one formula, shared uniforms: the ground shader and every prop/tower/unit material must agree on where
 * the shade is, or a tree stands sunlit in the middle of a shadowed meadow. The ground, its gorge walls and the
 * pollen motes (`groundMesh.ts`, `views/meadow.ts`) use `glsl`/`applyGlsl` and these same uniform objects, so there is
 * one formula and one `uCloudAmount` for everything.
 *
 * Cost: ~10 ALU per pixel (two value-noise lookups), one varying. No new samplers, no new lights.
 */
export class CloudShadows {
  /**
   * Shared uniforms. `uCloudTime` is seconds (SceneRig.update advances it). `uCloudAmount` scales the whole effect
   * (0 = no cloud shadows, 1 = the formula as written) — the "boss dusk" hero moment can push it past 1 for heavier
   * cover without editing the formula. `uCloudTint` is the faint cool cast in the shade (linear RGB).
   */
  static readonly uniforms = {
    uCloudTime: { value: 0 },
    uCloudAmount: { value: 1 },
    uCloudTint: { value: new THREE.Color(0xb8c8ff) },
  };

  /** How far toward `uCloudTint` the direct light moves at full shade: faint on purpose (the F1 grade fix is about removing blue). */
  static readonly TintMix = 0.12;
  /** How much of the direct light the heart of a cloud takes away (0.30: it keeps 70 %). */
  static readonly MaxShade = 0.30;

  /**
   * Declarations + functions for a fragment shader. `cloudShadow(xz)` returns the direct-light multiplier:
   * 1.0 in sun, 0.70 at the heart of a cloud. `cloudShade(xz)` wraps it with `uCloudAmount` and returns
   * (multiplier, shade 0..1) for the tint.
   */
  static readonly glsl = /* glsl */ `
uniform float uCloudTime;
uniform float uCloudAmount;
uniform vec3 uCloudTint;
float csHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(csHash(i), csHash(i + vec2(1.0, 0.0)), f.x), mix(csHash(i + vec2(0.0, 1.0)), csHash(i + vec2(1.0, 1.0)), f.x), f.y);
}
float cloudShadow(vec2 xz) {
  vec2 p = xz * 0.045 + uCloudTime * vec2(0.018, 0.011);
  float n = vnoise(p) * 0.65 + vnoise(xz * 0.11 + 3.1) * 0.35;
  return 1.0 - ${CloudShadows.MaxShade.toFixed(2)} * smoothstep(0.42, 0.68, n);
}
vec2 cloudShade(vec2 xz) {
  float cs = clamp(1.0 - (1.0 - cloudShadow(xz)) * uCloudAmount, 0.0, 1.0);
  return vec2(cs, (1.0 - cs) / ${CloudShadows.MaxShade.toFixed(2)});
}
`;

  /** The lines that apply it, for a fragment shader after the direct light has been accumulated. */
  static applyGlsl(worldPos: string): string {
    return /* glsl */ `
  {
    vec2 csv = cloudShade(${worldPos}.xz);
    vec3 csTint = mix(vec3(1.0), uCloudTint, ${CloudShadows.TintMix.toFixed(3)} * clamp(csv.y, 0.0, 1.0));
    reflectedLight.directDiffuse *= csv.x * csTint;
    reflectedLight.directSpecular *= csv.x;
  }`;
  }

  /**
   * Adds cloud shadows to a built-in lit material's shader inside `onBeforeCompile` (MeshStandard/Physical, Lambert,
   * Phong, Toon — anything with `#include <lights_fragment_end>`). Brings its own world-position varying, so it does
   * not depend on other patches; instancing and rigid-rig skinning are handled because it reads `transformed` after
   * skinning and applies the instance matrix itself. Returns false (and changes nothing) if the anchors are missing.
   */
  static inject(shader: { uniforms: Record<string, THREE.IUniform>; vertexShader: string; fragmentShader: string }): boolean {
    if (!shader.vertexShader.includes("#include <worldpos_vertex>") || !shader.fragmentShader.includes("#include <lights_fragment_end>")) return false;
    if (shader.fragmentShader.includes("float cloudShadow(")) return true;             // already patched (a twin, a re-compile)
    Object.assign(shader.uniforms, CloudShadows.uniforms);
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vCloudWP;")
      .replace("#include <worldpos_vertex>", `#include <worldpos_vertex>
  #ifdef USE_INSTANCING
    vCloudWP = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
  #else
    vCloudWP = (modelMatrix * vec4(transformed, 1.0)).xyz;
  #endif`);
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\nvarying vec3 vCloudWP;\n${CloudShadows.glsl}`)
      .replace("#include <lights_fragment_end>", `#include <lights_fragment_end>${CloudShadows.applyGlsl("vCloudWP")}`);
    return true;
  }

  /** Advances the drift; call once per frame with the frame's dt (seconds). */
  static update(dt: number): void { CloudShadows.uniforms.uCloudTime.value += dt; }
}
